import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { getOfferCancellationReasonsResponseSchema, offersApiSchema } from "../schemas/offer";
import { statusApiSchema } from "../schemas/status";

export const offersApi = {
    getOffers: async (axios, params) => {
        return axios
            .get(apiUrls.offers.all, { params })
            .then((response) => offersApiSchema.getOffersResponse.parse(response.data));
    },
    getCustomerOffers: async (axios, customerId, params) => {
        return axios
            .get(apiUrls.offers.customer(customerId), { params })
            .then((response) => offersApiSchema.getOffersResponse.parse(response.data));
    },
    getProductOffers: async (axios, productId) => {
        return axios
            .get(apiUrls.offers.product(productId))
            .then((response) => offersApiSchema.getOffersResponse.parse(response.data));
    },
    getOffer: async (axios, offerId) => {
        return axios
            .get(apiUrls.offers.detail(offerId))
            .then((response) => offersApiSchema.offer.parse(response.data));
    },
    createOffer: async (axios, data) => {
        return axios
            .post(apiUrls.offers.all, offersApiSchema.postCreateOfferRequest.parse(data))
            .then((response) => response.data);
    },
    updateOffer: async (axios, offerId, data) => {
        return axios
            .put(apiUrls.offers.update(offerId), offersApiSchema.putUpdateOfferRequest.parse(data))
            .then((response) => response.data);
    },
    getOfferStatuses: async (axios) => {
        return axios
            .get(apiUrls.offers.statuses)
            .then((response) => statusApiSchema.getStatusesResponse.parse(response.data));
    },
    getOfferCancellationReasons: async (axios) => {
        return axios
            .get(apiUrls.offers.reasons)
            .then((response) => getOfferCancellationReasonsResponseSchema.parse(response.data));
    },
    updateOfferStatus: async (axios, offerId, statusId, reasonId, statusNote) => {
        const payload = { status_id: statusId };
        if (reasonId) {
            payload.reason_id = reasonId;
        }
        if (statusNote) {
            payload.status_note = statusNote;
        }
        return axios.post(apiUrls.offers.setStatus(offerId), payload);
    },
    sendOffer: async (axios, offerId, data) => {
        return axios
            .post(apiUrls.offers.send(offerId), data, {headers: {"Content-Type": "multipart/form-data"}})
            // .post(apiUrls.offers.send(offerId), offersApiSchema.postSendOfferRequest.parse(data))
            .then((response) => response.data);
    },
};

export function useOffers(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["offers", Object.fromEntries(params.entries())],
        queryFn: () => {
            return offersApi.getOffers(axiosPriv, params);
        },
    });
}

export function useOfferById(offerId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["offers", offerId],
        queryFn: () => {
            return offersApi.getOffer(axiosPriv, offerId);
        },
        enabled: !!offerId,
    });
}

export function useCustomerOffers(customerId, params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["customerOffers", customerId, Object.fromEntries(params.entries())],
        queryFn: () => {
            return offersApi.getCustomerOffers(axiosPriv, customerId, params);
        },
        enabled: !!customerId,
    });
}

export function useProductOffers(productId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["productOffers", productId],
        queryFn: () => {
            return offersApi.getProductOffers(axiosPriv, productId);
        },
        enabled: !!productId,
    });
}

export function useCreateOfferMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["offers"] });
            await queryClient.invalidateQueries({ queryKey: ["customerOffers"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (body) => offersApi.createOffer(axiosPriv, body),
        onError: onServerError,
    });
}

export function useUpdateOfferMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["offers", variables.offerId] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ offerId, data }) => offersApi.updateOffer(axiosPriv, offerId, data),
        onError: onServerError,
    });
}

export function useSendOfferMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["offers"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ offerId, data }) => offersApi.sendOffer(axiosPriv, offerId, data),
        onError: onServerError,
    });
}

export function useOfferStatuses(options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["offerStatuses"],
        queryFn: () => {
            return offersApi.getOfferStatuses(axiosPriv);
        },
    });
}

export function useOfferCancellationReasons(options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["offerCancellationReasons"],
        queryFn: () => {
            return offersApi.getOfferCancellationReasons(axiosPriv);
        },
    });
}

export function useUpdateOfferStatusMutation() {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ offerId, statusId, reasonId, statusNote }) => {
            return offersApi.updateOfferStatus(axiosPriv, offerId, statusId, reasonId, statusNote);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["offers"] });
            queryClient.invalidateQueries({ queryKey: ["customerOffers"] });
        },
    });
}
